import { useState } from 'react';
import { motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson } from '@/mocks/lessons';
import Pressable from '@/components/base/Pressable';

interface LessonMetaProps {
  lesson: SavedLesson;
  activeSection: string;
  onSectionChange: (section: string) => void;
}

const sections = [
  { id: 'overview', label: 'Overview', icon: 'ri-file-list-line' },
  { id: 'practice', label: 'Practice', icon: 'ri-questionnaire-line' },
  { id: 'worksheets', label: 'Worksheets', icon: 'ri-draft-line' },
  { id: 'homework', label: 'Homework Help', icon: 'ri-lightbulb-line' },
  { id: 'related', label: 'Related', icon: 'ri-movie-line' },
];

function difficultyColor(difficulty: string, isDark: boolean) {
  switch (difficulty) {
    case 'Beginner': return isDark ? 'text-emerald-400 bg-emerald-500/[0.08]' : 'text-emerald-600 bg-emerald-50';
    case 'Intermediate': return isDark ? 'text-amber-400 bg-amber-500/[0.08]' : 'text-amber-600 bg-amber-50';
    case 'Advanced': return isDark ? 'text-rose-400 bg-rose-500/[0.08]' : 'text-rose-600 bg-rose-50';
    default: return isDark ? 'text-black/40 bg-black/[0.04]' : 'text-neutral-400 bg-neutral-100';
  }
}

export default function LessonMeta({ lesson, activeSection, onSectionChange }: LessonMetaProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [expanded, setExpanded] = useState(false);
  const [liked, setLiked] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-white/50' : 'text-black/50',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    chipBg: isDark ? 'bg-white/[0.03]' : 'bg-black/[0.03]',
    chipBorder: isDark ? 'border-white/[0.04]' : 'border-black/[0.04]',
    iconMuted: isDark ? 'text-white/25' : 'text-black/25',
    sectionActive: isDark ? 'bg-white text-black' : 'bg-black text-white',
    sectionInactive: isDark ? 'bg-white/[0.04] text-white/40 hover:bg-white/[0.08]' : 'bg-black/[0.04] text-black/40 hover:bg-black/[0.08]',
    actionBtnActive: isDark ? 'bg-white text-black' : 'bg-black text-white',
    actionBtn: isDark ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08]' : 'bg-black/[0.04] text-black/60 hover:bg-black/[0.08]',
    shareMenu: isDark ? 'bg-[#161618] border-white/[0.08]' : 'bg-white border-black/[0.08]',
    shareText: isDark ? 'text-white/60' : 'text-black/60',
    notesCard: isDark ? 'bg-amber-500/[0.04] border-amber-500/10' : 'bg-amber-50 border-amber-200/50',
    notesText: isDark ? 'text-white/55' : 'text-black/60',
    notesIcon: isDark ? 'text-amber-400' : 'text-amber-500',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pt-4 pb-1"
    >
      {/* Title Row */}
      <h1 className={`${t.textPrimary} text-base font-bold leading-snug mb-2`}>
        {lesson.title}
      </h1>

      {/* Lesson Stats Row */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`ri-time-line ${t.iconMuted} text-xs`} />
          </div>
          <span className={`${t.textMuted} text-[11px]`}>{formatDuration(lesson.duration)}</span>
        </div>
        <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/15' : 'bg-black/15'}`} />

        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`ri-bar-chart-box-line ${t.iconMuted} text-xs`} />
          </div>
          <span className={`${t.textMuted} text-[11px]`}>{lesson.completionPercent}% done</span>
        </div>
        <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/15' : 'bg-black/15'}`} />

        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${difficultyColor(lesson.topicDifficulty, isDark)}`}>
          {lesson.topicDifficulty}
        </span>
        <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/15' : 'bg-black/15'}`} />

        <span className={`${isDark ? 'bg-white/[0.05] text-white/40' : 'bg-black/[0.04] text-black/40'} px-2 py-0.5 rounded-full text-[10px] font-medium`}>{lesson.subject}</span>
      </div>

      {/* Action Row */}
      <div className="flex items-center gap-2 mb-4">
        <Pressable
          onClick={() => setLiked(!liked)}
          className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold transition-all duration-200 ${
            liked ? t.actionBtnActive : t.actionBtn
          }`}
          scale={0.92}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`${liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-sm`} />
          </div>
          {liked ? 'Liked' : 'Like'}
        </Pressable>

        <div className="relative">
          <Pressable
            onClick={() => setShowShare(!showShare)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs font-semibold transition-all duration-200 ${t.actionBtn}`}
            scale={0.92}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-share-line text-sm" />
            </div>
            Share
          </Pressable>

          {showShare && (
            <div
              className={`absolute top-full mt-2 left-0 ${t.shareMenu} border rounded-2xl p-3 w-[200px] z-30`}
              style={{ animation: 'fadeIn 0.2s ease-out' }}
            >
              <div className="space-y-1">
                {[
                  { icon: 'ri-link', label: 'Copy Link' },
                  { icon: 'ri-message-3-line', label: 'Send to Chat' },
                  { icon: 'ri-mail-line', label: 'Email' },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => setShowShare(false)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left ${t.shareText} text-xs font-medium ${isDark ? 'hover:bg-white/[0.04]' : 'hover:bg-black/[0.04]'} transition-colors cursor-pointer`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${item.icon} text-sm`} />
                    </div>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="mb-3">
        <div className={`${t.textMuted} text-xs leading-relaxed`}>
          {lesson.description}
        </div>
      </div>

      {/* Educational Meta Row */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${t.chipBg} border ${t.chipBorder}`}>
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`ri-hourglass-line ${t.iconMuted} text-[10px]`} />
          </div>
          <span className={`${t.textMuted} text-[11px]`}>~{lesson.estimatedStudyTime} min study</span>
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${t.chipBg} border ${t.chipBorder}`}>
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`ri-history-line ${t.iconMuted} text-[10px]`} />
          </div>
          <span className={`${t.textMuted} text-[11px]`}>Last: {lesson.lastWatched}</span>
        </div>
        <button
          onClick={() => setShowNotes(!showNotes)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg ${t.chipBg} border ${t.chipBorder} cursor-pointer active:scale-95 transition-transform duration-100`}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`ri-sparkling-line ${t.notesIcon} text-[10px]`} />
          </div>
          <span className={`${t.textMuted} text-[11px]`}>AI Tutor Notes</span>
          <div className="w-3 h-3 flex items-center justify-center">
            <i className={`${showNotes ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} ${t.iconMuted} text-[10px]`} />
          </div>
        </button>
      </div>

      {/* AI Tutor Notes (expandable) */}
      {showNotes && (
        <div
          className={`mb-4 p-3 rounded-xl ${t.notesCard} border`}
          style={{ animation: 'fadeIn 0.25s ease-out' }}
        >
          <div className="flex items-start gap-2">
            <div className={`w-6 h-6 rounded-lg ${isDark ? 'bg-amber-500/10' : 'bg-amber-100'} flex items-center justify-center flex-shrink-0 mt-0.5`}>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`ri-sparkling-line ${t.notesIcon} text-[10px]`} />
              </div>
            </div>
            <p className={`${t.notesText} text-xs leading-relaxed`}>{lesson.aiTutorNotes}</p>
          </div>
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex gap-1 overflow-x-auto no-scrollbar pb-1 -mx-5 px-5">
        {sections.map((section) => (
          <motion.button
            key={section.id}
            onClick={() => onSectionChange(section.id)}
            whileTap={{ scale: 0.92 }}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 cursor-pointer transition-all duration-200 ${
              activeSection === section.id ? t.sectionActive : t.sectionInactive
            }`}
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={`${section.icon} text-[10px]`} />
            </div>
            {section.label}
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
}